/**
 * Loading the infuser under me (rules 27), when I carry more than one kind of block: a card per
 * kind with how many I carry; a tap loads that kind, as many as its hopper takes, and closes the
 * sheet. The planet stays free beside it; walking off the infuser closes it.
 */
import { BLOCKS, MATERIAL_OF } from '@bramvasterd/terrella-rules';
import type { JSX } from 'react';

import { MACHINE_COLOR } from '@core/palette';
import { loadInfuser } from '@game/options/options';
import { factsAt } from '@game/planet/here';
import { spendableOf } from '@game/planet/pockets';
import { closePanel } from '@game/view/panels';
import { machineName, resourceName, t } from '@i18n/index';
import { CardStrip } from '@ui/components/CardStrip';
import { HexPicture } from '@ui/components/HexPicture';
import { Sheet } from '@ui/components/Sheet';
import { useGame, useGameState, useLocale } from '@ui/hooks';

import '../build/build.css';

const count = (n: number): string => (Number.isFinite(n) ? String(n) : '∞');

export function LoadSheet(): JSX.Element | null {
  useLocale();
  const game = useGame();
  const tile = useGameState((s) => s.panelTile);
  useGameState((s) => s.planetVersion);
  const s = game.store.get();
  const machine = factsAt(game, tile)?.machine;
  if (machine?.role !== 'infuser') return null;
  const mine = spendableOf(s.view, s.me?.id ?? null);
  const blocks = BLOCKS.filter((block) => mine[block] > 0);
  const title = t('load.title', { name: machineName(machine.type).toLowerCase() });
  return (
    <Sheet
      id="load"
      title={title}
      onClose={() => closePanel(game)}
      className="load"
      tone={MACHINE_COLOR[machine.type]?.ui}
      free
    >
      <CardStrip label={title}>
        {blocks.map((block) => (
          <li key={block}>
            <button
              type="button"
              className="bcard"
              data-block={block}
              aria-label={t('load.pick-aria', { block: resourceName(block, 2).toLowerCase() })}
              onClick={() => {
                if (loadInfuser(game, block)) closePanel(game);
              }}
            >
              <HexPicture look={{ tile: MATERIAL_OF[block] }} />
              <span className="about">
                <b>{resourceName(block, 2)}</b>
                <em>{count(mine[block])}</em>
              </span>
            </button>
          </li>
        ))}
      </CardStrip>
      <p className="note">{t('load.note')}</p>
    </Sheet>
  );
}
